import api, { clearAxiosCache } from '../axios-config';

export interface BadgeDefinitionAPI {
  badgeId: string;
  badgeName: string;
  badgeSlug: string;
  badgeDescription?: string;
  badgeImageUrl?: string;
  badgeLevels: BadgeLevelAPI[];
}

export interface BadgeLevelAPI {
  badgeLevelId: number;
  badgeId?: string;
  levelName: string;
  levelNumber: number;
  levelDescription?: string;
  levelImageUrl?: string;
  levelPointsRequired: number;
}

export interface StudentBadgeProgressAPI {
  studentId: string;
  badgeId: string;
  currentXp: number;
  currentLevelId?: number;
  lastAwardedAt?: string;
  badge: BadgeDefinitionAPI;
  currentLevel?: BadgeLevelAPI;
}

export interface BadgeXpEventAPI {
  badgeXpEventId: number;
  studentId: string;
  badgeId: string;
  deltaXp: number;
  reason?: string;
  createdBy: string;
  createdAt: string;
  badge: BadgeDefinitionAPI;
  createdByUser: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface AwardXPRequest {
  studentId: string;
  badgeId: string;
  xpAmount: number;
  reason: string;
  pumaPoints?: number;
}

export interface BulkAwardXPRequest {
  pumaPoints?: number;
  awardXPRequests: AwardXPRequest[];
}

export interface BulkAwardXPResult {
  studentId: string;
  badgeId: string;
  success: boolean;
  newXP: number;
  currentLevel?: number;
}

// cache for student XP history promises
const historyCache: Record<string, Promise<BadgeXpEventAPI[]>> = {};

export const badgeService = {
  // Badge definitions
  async getBadges(): Promise<BadgeDefinitionAPI[]> {
    // Note: caching is handled internally by axios-config interceptor
    const response = await api.get('/api/badges');
    return response.data;
  },

  async getBadge(badgeId: string): Promise<BadgeDefinitionAPI> {
    const response = await api.get(`/api/badges/${badgeId}`);
    return response.data;
  },

  async createBadge(badge: Partial<BadgeDefinitionAPI>): Promise<BadgeDefinitionAPI> {
    const response = await api.post('/api/badges', badge);
    // Invalidate cached GETs so subsequent views reflect the new data
    try { clearAxiosCache(); } catch { /* ignore */ }
    return response.data;
  },

  async updateBadge(badgeId: string, badge: Partial<BadgeDefinitionAPI>): Promise<void> {
    await api.put(`/api/badges/${badgeId}`, badge);
    // Invalidate cached GETs for badges so re-entry shows updated data
    try { clearAxiosCache(); } catch { /* ignore */ }
  },

  async deleteBadge(badgeId: string): Promise<void> {
    await api.delete(`/api/badges/${badgeId}`);
    // Clear cache after deletion to avoid stale lists
    try { clearAxiosCache(); } catch { /* ignore */ }
  },

  // Student badge progress
  async getStudentBadgeProgress(studentId: string): Promise<StudentBadgeProgressAPI[]> {
    const response = await api.get(`/api/badgeprogress/student/${studentId}`);
    return response.data;
  },

  async getStudentBadgeProgressByBadge(studentId: string, badgeId: string): Promise<StudentBadgeProgressAPI> {
    const response = await api.get(`/api/badgeprogress/student/${studentId}/badge/${badgeId}`);
    return response.data;
  },

  // Award XP
  async awardXP(request: AwardXPRequest): Promise<{ success: boolean; newXP: number; currentLevel?: number }> {
    const response = await api.post('/api/badgeprogress/award', request);
    // clear cache for this student to refetch fresh history later
    delete historyCache[request.studentId];
    return response.data;
  },

  async awardBulkXP(request: BulkAwardXPRequest): Promise<BulkAwardXPResult[]> {
    const response = await api.post('/api/badgeprogress/award/bulk', request);
    // clear cache for all affected students
    request.awardXPRequests.forEach(r => delete historyCache[r.studentId]);
    return response.data;
  },

  // XP History
  async getStudentXPHistory(studentId: string): Promise<BadgeXpEventAPI[]> {
    // return cached promise if exists
    if (!historyCache[studentId]) {
      historyCache[studentId] = api.get(`/api/badgeprogress/student/${studentId}/history`)
        .then(response => response.data);
    }
    return historyCache[studentId];
  }
};
